<template>
  <v-navigation-drawer v-model="internalShow" :location="mobile ? 'bottom' : 'right'" name="ai-chat-drawer" order="1" :width="mobile ? undefined : '500'"
    :height="mobile ? '90vh' : undefined" border="s" elevation="4" class="d-flex flex-column" :class="{ 'rounded-t-xl': mobile }">
    <!-- Floating Close Button -->
    <v-btn
      icon="mdi-close"
      variant="elevated"
      density="comfortable"
      @click="handleClose"
      class="position-absolute"
      style="top: 16px; right: 16px; z-index: 10;"
    ></v-btn>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-4 mt-4 mb-0">
      {{ error }}
    </v-alert>

    <!-- Chat Messages Area -->
    <div class="chat-messages pa-4 pt-12 flex-grow-1 overflow-y-auto d-flex flex-column" ref="messagesContainer">
      <div v-for="(msg, index) in messages" :key="index" class="d-flex mb-4"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'">
        <v-avatar v-if="msg.role === 'ai'" color="primary" size="36" class="mr-3 mt-1">
          <v-icon color="white" size="20">mdi-robot-outline</v-icon>
        </v-avatar>

        <div class="message-bubble pa-3 rounded-lg"
          :class="msg.role === 'user' ? 'bg-primary text-white user-bubble' : 'bg-grey-lighten-4 ai-bubble'"
          style="max-width: 85%;">

          <div v-if="msg.role === 'ai'" class="text-body-1 markdown-body" v-html="DOMPurify.sanitize(marked.parse(msg.content))"
            @click="handleMarkdownClick"></div>
          <div v-else class="text-body-1">{{ msg.content }}</div>

          <div v-if="msg.role === 'ai'" class="text-caption text-grey-darken-1 disclaimer mt-2">
            Axle can make mistakes. Call <a href="tel:12265738131">226-573-8131</a> or email
            <a href="mailto:support@readychek.com">support@readychek.com</a> if you need help.
          </div>

          <div class="d-flex align-center justify-space-between mt-1">
            <div v-if="msg.role === 'ai' && msg.id" class="d-flex align-center">
              <v-btn icon variant="text" size="x-small" :color="msg.liked === 1 ? 'green' : 'grey'"
                @click="rateMessage(msg, 1)">
                <v-icon size="16">{{ msg.liked === 1 ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="x-small" :color="msg.liked === -1 ? 'red' : 'grey'"
                @click="rateMessage(msg, -1)">
                <v-icon size="16">{{ msg.liked === -1 ? 'mdi-thumb-down' : 'mdi-thumb-down-outline' }}</v-icon>
              </v-btn>
            </div>
            <div v-else></div>
            <div class="text-caption text-right" :class="msg.role === 'user' ? 'text-white-opacity-70' : 'text-grey'">
              {{ formatTime(msg.timestamp) }}
            </div>
          </div>

        </div>
      </div>

      <div v-if="isTyping" class="d-flex mb-4 justify-start">
        <v-avatar color="primary" size="36" class="mr-3 mt-1">
          <v-icon color="white" size="20">mdi-robot-outline</v-icon>
        </v-avatar>
        <div class="message-bubble pa-3 rounded-lg bg-grey-lighten-4 ai-bubble d-flex align-center">
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="pa-4 bg-white border-t flex-shrink-0" style="border-top: 1px solid #e0e0e0;">
      <v-form @submit.prevent="sendPrompt">
        <v-text-field v-model="newMessage" placeholder="Ask about ReadyChek, Hours of Service..." variant="outlined"
          density="comfortable" hide-details class="rounded-lg" bg-color="white">
          <template v-slot:append-inner>
            <v-btn icon="mdi-send" variant="text" color="primary" :disabled="!newMessage.trim() || isTyping"
              @click="sendPrompt" />
          </template>
        </v-text-field>
      </v-form>
    </div>
  </v-navigation-drawer>

  <!-- Image Preview Lightbox -->
  <v-dialog v-model="showImagePreview" max-width="1000" min-height="100">
    <v-card class="bg-black">
      <div class="d-flex justify-end pa-2 position-absolute" style="top: 0; right: 0; z-index: 10;">
        <v-btn icon="mdi-close" variant="flat" color="blue" @click="showImagePreview = false"></v-btn>
      </div>
      <v-img :src="previewImageUrl" max-height="85vh" class="ma-4"></v-img>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue';
import { useDisplay } from 'vuetify';

import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAiChatService } from '@/v3/composables/useAiChatService';
import { useAiContext } from '@/v3/composables/useAiContext';

// Configure marked to open links in a new tab
marked.use({
  renderer: {
    link(token) {
      const titleAttr = token.title ? ` title="${token.title}"` : '';
      return `<a href="${token.href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${token.text}</a>`;
    }
  }
});
const props = defineProps({
  modelValue: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'closeDialog']);

const internalShow = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const { mobile } = useDisplay();
const { pageTitle, pageContext } = useAiContext();
const aiService = useAiChatService();

const error = ref(null);
const messages = ref([
  {
    role: 'ai',
    content: 'Hello! I am Axle™, your ReadyChek AI Assistant. How can I help you today with Hours of Service, Inspections, or anything else ReadyChek related?',
    timestamp: new Date()
  }
]);

const newMessage = ref('');
const isTyping = ref(false);
const messagesContainer = ref(null);
const showImagePreview = ref(false);
const previewImageUrl = ref('');

const handleMarkdownClick = (e) => {
  if (e.target.tagName === 'IMG') {
    previewImageUrl.value = e.target.src;
    showImagePreview.value = true;
  }
};

const scrollToBottom = async () => {
  await nextTick();
  setTimeout(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, 100);
};

const formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const handleClose = () => {
  internalShow.value = false;
  emit('closeDialog');
};

const rateMessage = (msg, likedValue) => {
  if (msg.liked === likedValue) return; // Ignore if already selected
  msg.liked = likedValue;
  savePrompt(msg, likedValue);
};

const savePrompt = async (message, liked) => {
  aiService.savePrompt(message.id, liked);
}

const sendPrompt = async () => {
  if (!newMessage.value.trim() || isTyping.value) return;

  const userText = newMessage.value.trim();
  messages.value.push({
    role: 'user',
    content: userText,
    timestamp: new Date()
  });

  newMessage.value = '';
  scrollToBottom();

  // Simulate AI response
  isTyping.value = true;
  scrollToBottom();

  const params = {
    prompt: userText,
    pageName: pageTitle.value,
    pageContext: pageContext.value
  };

  aiService.sendPrompt(
    params,
    (response) => {
      messages.value.push({
        role: 'ai',
        id: response.id,
        content: response.content,
        timestamp: new Date(),
        liked: null,
      });
    },
    (errorMessage) => {
      messages.value.push({
        role: 'ai',
        content: errorMessage,
        timestamp: new Date()
      });
    },
    () => {
      isTyping.value = false;
      scrollToBottom();
    }
  );
};

onMounted(() => {
  scrollToBottom();
});
</script>

<style scoped>

/* Vuetify wraps the drawer's slot content in .v-navigation-drawer__content, so the
   flex-column on the drawer itself never reaches these children. Without this the
   messages area grows to fit the conversation instead of scrolling, pushing the
   input area out of the drawer once an answer is long enough. */
:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-messages {
  background-color: #f8f9fa;
  /* flex items default to min-height: auto, which would let this grow past the drawer */
  min-height: 0;
}

.user-bubble {
  border-bottom-right-radius: 4px !important;
}

.ai-bubble {
  border-bottom-left-radius: 4px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Typing Indicator Animation */
.typing-indicator {
  display: flex;
  align-items: center;
  height: 20px;
}

.typing-indicator span {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #90a4ae;
  border-radius: 50%;
  margin: 0 2px;
  animation: typing 1.4s infinite both;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.disclaimer {
  line-height: 1.3;
}

.disclaimer a {
  color: inherit;
  text-decoration: underline;
}

/* Markdown Styles */
.markdown-body :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-body :deep(a) {
  color: #1976D2;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.markdown-body :deep(code) {
  background-color: #eeeeee;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
}

.markdown-body :deep(pre) {
  background-color: #eeeeee;
  padding: 0.8rem;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 0.5rem;
}

.markdown-body :deep(img) {
  max-width: 320px;
  height: auto;
  border-radius: 8px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.markdown-body :deep(img:hover) {
  transform: scale(1.05);
}
</style>
